import { SCALING_TYPES } from 'constants/feature/statement-creation-constants';
import { removeCommasFromNumber } from 'utils/number-formatter-util';

/**
 * Utility function to check if a number has 16 or more digits
 * @param {number} number - The number to check
 * @returns {boolean} - True if the number has 16+ digits
 */
export const isLargeNumber = (number) => {
  const digitCount = Math.abs(number)
    .toString()
    .replace('.', '')
    .replace('-', '').length;
  return digitCount >= 16;
};

export const shouldDisplayValueBeFlagged = (
  elementDetails,
  formulaInProgress,
) => {
  // if this case is triggered, the formula won't be considered to be flagged but we stil
  // want to have the text in the orange color
  /* this case is not going to be applied if the formula original value
   and the formula computedResult are still the same number after applying the "/" operator */
  if (
    formulaInProgress.shouldValueBeComparedToRoundedValue(elementDetails) &&
    !formulaInProgress.isFormulaFlagged(elementDetails) &&
    !(
      parseFloat(formulaInProgress.computedResult) ===
      parseFloat(removeCommasFromNumber(formulaInProgress.display))
    )
  ) {
    return true;
  }

  return (
    formulaInProgress.isFormulaFlagged(elementDetails) &&
    !elementDetails.isSystemOverrideFlagged()
  );
};
export const round = (value, exp) => {
  if (typeof exp === 'undefined' || +exp === 0) return Math.round(value);

  value = +value;
  exp = +exp;

  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) return NaN;

  // Shift
  value = value.toString().split('e');
  value = Math.round(+(value[0] + 'e' + (value[1] ? +value[1] + exp : exp)));

  // Shift back
  value = value.toString().split('e');
  return +(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp));
};
export const countDecimals = (number) => {
  let numberToString = Math.abs(number).toString();
  let index = numberToString.indexOf('.');
  if (index !== -1) {
    return numberToString.length - index - 1;
  }
  return 0;
};

export const countDigits = (number) => {
  let numberToString = Math.abs(number).toString();
  let index = numberToString.indexOf('.');
  if (index !== -1) {
    return index;
  }
  return numberToString.length;
};

// Number format configurations - moved outside function for better performance
const FORMAT_CONFIGS = {
  1: { thousandSep: ',', decimalSep: '.' },
  2: { thousandSep: '.', decimalSep: ',' },
  3: { thousandSep: "'", decimalSep: '.' },
  4: { thousandSep: ' ', decimalSep: '.' },
  5: { thousandSep: ' ', decimalSep: ',' },
  6: { thousandSep: "'", decimalSep: ',' },
};

// Cached regex for better performance
const SCIENTIFIC_NOTATION_REGEX = /[eE]/;
const NUMERIC_REGEX = /[0-9]/;
const THOUSAND_SEPARATOR_REGEX = /\B(?=(\d{3})+(?!\d))/g;

// Cache for frequently formatted values to improve performance
const formatCache = new Map();
const CACHE_SIZE_LIMIT = 1000; // Prevent memory leaks

// Optimized helper functions
const isScientificNotation = (str) => SCIENTIFIC_NOTATION_REGEX.test(str);
const hasNumbers = (str) => NUMERIC_REGEX.test(str);
const isLargeString = (str) => str.length > 15;

// generic function which is used to replace the custom special characters separators for thousands and decimal places for each amount element
export const numberFormatConverter = (
  number,
  thousandSep = ',',
  decimalSep = '.',
) => {
  // Create cache key for common values
  const cacheKey = `${number}_${thousandSep}_${decimalSep}`;

  // Check cache for previously computed values
  if (formatCache.has(cacheKey)) {
    return formatCache.get(cacheKey);
  }

  let numStr;

  // Fast path for strings - avoid redundant checks
  if (typeof number === 'string') {
    numStr = number;
  } else if (typeof number === 'number') {
    // Cache toString() result to avoid multiple calls
    const numberStr = number.toString();

    if (isScientificNotation(numberStr)) {
      // Handle scientific notation efficiently
      if (number % 1 === 0) {
        numStr = number.toFixed(0);
      } else {
        // Extract decimal places only once
        const scientificParts = numberStr.split('e')[0];
        const decimalPart = scientificParts.split('.')[1];
        const decimalPlaces = decimalPart ? decimalPart.length : 0;
        numStr = number.toFixed(Math.max(decimalPlaces, 6));
      }
    } else if (number > Number.MAX_SAFE_INTEGER) {
      numStr = number % 1 === 0 ? number.toFixed(0) : numberStr;
    } else {
      numStr = numberStr;
    }
  } else {
    numStr = String(number);
  }

  // Use indexOf for better performance than split
  const dotIndex = numStr.indexOf('.');
  const intPart = dotIndex === -1 ? numStr : numStr.slice(0, dotIndex);
  const decimalPart = dotIndex === -1 ? '' : numStr.slice(dotIndex + 1);

  // Apply thousand separators only if needed (performance optimization)
  const formattedIntPart =
    intPart.length > 3
      ? intPart.replace(THOUSAND_SEPARATOR_REGEX, thousandSep)
      : intPart;

  // Use ternary for slight performance improvement
  const result = decimalPart
    ? `${formattedIntPart}${decimalSep}${decimalPart}`
    : formattedIntPart;

  // Cache the result if cache is not too large
  if (formatCache.size < CACHE_SIZE_LIMIT) {
    formatCache.set(cacheKey, result);
  }

  return result;
};

// gets the formatted amount value based on the numberformat selected during the statement upload for this current statement
export const getElementResultByNumberFormat = (
  value,
  numberFormatId,
  scalingId = 0,
) => {
  /** null or undefined check */
  if (!value) return value;

  // Early return for Indian number formatting to avoid unnecessary processing
  if (scalingId === SCALING_TYPES.LAKHS || scalingId === SCALING_TYPES.CRORES) {
    const numValue =
      typeof value === 'string' && hasNumbers(value)
        ? parseFloat(value)
        : value;
    return Number(numValue).toLocaleString('en-IN');
  }

  // Get format configuration efficiently using the global config
  const config = FORMAT_CONFIGS[numberFormatId] || FORMAT_CONFIGS[1];
  const { thousandSep, decimalSep } = config;

  // Handle very large numbers that might be in scientific notation
  // Optimized value processing
  let unformattedNumber;
  const isStringValue = typeof value === 'string';
  const isNumberValue = typeof value === 'number';

  if (isStringValue && hasNumbers(value)) {
    unformattedNumber =
      isLargeString(value) || isScientificNotation(value)
        ? value
        : parseFloat(value);
  } else if (
    isNumberValue &&
    (isScientificNotation(value.toString()) || value > Number.MAX_SAFE_INTEGER)
  ) {
    const valueStr = value.toString();
    if (value % 1 === 0) {
      unformattedNumber = value.toFixed(0);
    } else if (isScientificNotation(valueStr)) {
      const decimalPlaces = valueStr.split('e')[0].split('.')[1]?.length || 0;
      unformattedNumber = value.toFixed(Math.max(decimalPlaces, 2));
    } else {
      unformattedNumber = valueStr;
    }
  } else {
    unformattedNumber = value;
  }

  return numberFormatConverter(unformattedNumber, thousandSep, decimalSep);
};
